'use client';
import { ArrowUpCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

function BackToTop() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Use a ref to track if component is mounted
  const isMounted = useRef(true);

  useEffect(() => {
    const handleScroll = () => {
      // Only update state if component is still mounted
      if (isMounted.current) {
        // Calculate scroll progress percentage
        const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = Math.min(window.scrollY / totalHeight, 1);
        setScrollProgress(progress);

        // Show button after scrolling down 300px
        setShowScrollButton(window.scrollY > 300);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Initial check
    handleScroll();

    return () => {
      isMounted.current = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Progress bar at the top */}
      <div className="fixed top-0 left-0 z-50 h-1 w-full">
        <div
          className="h-full origin-left bg-gradient-to-r from-green-500 to-blue-500 dark:from-purple-500 dark:to-indigo-500"
          style={{
            width: `${scrollProgress * 100}%`,
            transition: 'width 0.1s ease-out',
          }}
        />
      </div>

      {/* Back to top button */}
      <div
        className={`fixed right-8 bottom-8 z-40 transition-all duration-300 ${
          showScrollButton
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-5 opacity-0'
        }`}
      >
        <button
          className="rounded-full bg-gradient-to-r from-green-800 to-blue-800 p-3 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:from-green-900 hover:to-blue-900 active:scale-95 dark:from-purple-800 dark:to-blue-800 dark:hover:from-purple-900 dark:hover:to-blue-900"
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <ArrowUpCircle className="h-6 w-6" />
        </button>
      </div>
    </>
  );
}

export default BackToTop;
